import { Box, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import AgyleButton from '../../../../../components/ui/buttons/agyleButton';
import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import AgyleTextField from '../../../../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../../../../components/ui/fields/agyleDatePicker';
import InsertCommentOutlinedIcon from '@mui/icons-material/InsertCommentOutlined';
import AgyleSelectField from '../../../../../components/ui/selector/agyleSelectField';
import ObsNumericStepper from '../../observation/components/obsNumericStepper';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '5px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
    },
    topBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 30,
    },
    button: {
        marginLeft: 'auto',
    },
    noPadding: {
        padding: 0,
    },
}));

const MedicationInput = ({
    type,
    title,
    field,
    value,
    onChange,
    onBlur,
    hideComment = false,
    disabled = false,
    error = false,
    ...props
}) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const [showComment, setShowComment] = useState(!isEmpty(value?.comment));
    const alwaysDisabled = type === 'route' || type === 'form';

    const [val, setVal] = useState(value);
    const [pendingChange, setPendingChange] = useState(false);

    useEffect(() => {
        setVal(value);
        setPendingChange(false);
    }, [value]);

    const handleToggleComment = () => {
        setShowComment(!showComment);
        if (!showComment) {
            const newVal = {
                ...val,
                comment: '',
            };
            setVal(newVal);
            setPendingChange(true);
            if (onChange) onChange({ field, value: newVal });
        }
    };

    const handleChangeInput = (e) => {
        const { name, value } = e.target;
        const newVal = {
            ...val,
            [name || 'value']: value,
        };
        setVal(newVal);
        setPendingChange(true);
        if (onChange) {
            onChange({ field, value: newVal });
        }
    };

    const handleChangeDateInput = (value) => {
        const newVal = {
            ...val,
            value: value,
        };
        setVal(newVal);
        setPendingChange(true);
        if (onChange) {
            onChange({ field, value: newVal });
        }
    };

    const handleChangeNumericStepperInput = (value) => {
        const newVal = {
            ...val,
            value: value,
        };
        setVal(newVal);
        setPendingChange(true);
        if (onChange) {
            onChange({ field, value: newVal });
        }
    };

    const handleChangeSelectInput = (value) => {
        const newVal = {
            ...val,
            value: value,
        };
        setVal(newVal);
        setPendingChange(true);
        if (onChange) {
            onChange({ field, value: newVal });
        }
    };

    const handleOnBlur = () => {
        if (pendingChange) {
            setPendingChange(false);
            if (onBlur) {
                onBlur({ field, value: val });
            }
        }
    };

    const renderInputByType = () => {
        switch (type) {
            case 'date':
                return (
                    <AgyleDatePicker
                        type="date"
                        label={t(title)}
                        value={val?.value ?? null}
                        onChange={handleChangeDateInput}
                        onBlur={handleOnBlur}
                        noBorder={false}
                        withNewBackground
                        disableFuture={false}
                        size="small"
                        disabled={disabled}
                        error={error}
                    />
                );
            case 'select':
                return (
                    <AgyleSelectField
                        withNewBackground
                        noBorder={false}
                        options={props?.options}
                        disabled={disabled}
                        label={t(title)}
                        value={val?.value ?? ''}
                        onChange={handleChangeSelectInput}
                        clearButtonVisible={props?.clearButtonVisible}
                        error={error}
                    />
                );
            case 'number':
                return (
                    <AgyleTextField
                        name="value"
                        type="number"
                        min={props?.min}
                        max={props?.max}
                        fullWidth
                        size="small"
                        noBorder={false}
                        withNewBackground
                        placeholder={title}
                        disabled={disabled || alwaysDisabled}
                        value={val?.value || 0}
                        onChange={handleChangeInput}
                        onBlur={handleOnBlur}
                        error={error}
                        inputProps={{
                            step: props?.step || 'any',
                            inputMode: 'decimal',
                        }}
                        pattern="[0-9]*"
                        inputMode={'decimal'}
                    />
                );
            case 'numericStepper':
                return (
                    <ObsNumericStepper
                        value={val?.value || 0}
                        minValue={props?.minValue}
                        maxValue={props?.maxValue}
                        // label={title}
                        label={''}
                        onChange={handleChangeNumericStepperInput}
                        disabled={disabled || alwaysDisabled}
                        noBackground
                        medicationInput
                        // column
                        error={error}
                    />
                );
            default:
                return (
                    <AgyleTextField
                        name="value"
                        fullWidth
                        size="small"
                        noBorder={false}
                        withNewBackground
                        placeholder={title}
                        disabled={disabled || alwaysDisabled}
                        value={val?.value || ''}
                        onChange={handleChangeInput}
                        onBlur={handleOnBlur}
                        error={error}
                    />
                );
        }
    };

    return (
        <Grid item xs={props?.xs || 12} sm={props?.sm || 6} className={classes.root}>
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} className={classes.topBox}>
                <Typography variant="body2" fontWeight={500}>
                    {t(title)}
                </Typography>

                {!hideComment && (
                    <AgyleButton
                        startIcon={<InsertCommentOutlinedIcon />}
                        variant="text"
                        className={classes.button}
                        onClick={handleToggleComment}
                        disabled={disabled}
                        size="small"
                    >
                        {t('Comment')}
                    </AgyleButton>
                )}
            </Box>

            {renderInputByType()}

            {!hideComment && showComment && (
                <AgyleTextField
                    name="comment"
                    label={t('Comment')}
                    fullWidth
                    style={{ marginTop: 5 }}
                    size="small"
                    noBorder={false}
                    withNewBackground
                    disabled={disabled}
                    value={val?.comment || ''}
                    onChange={handleChangeInput}
                    onBlur={handleOnBlur}
                    multiline
                />
            )}
        </Grid>
    );
};

export default MedicationInput;
